import { forwardRef } from "react";
import ButtonLink, { ButtonLinkProps } from "../ButtonLink";
import { ToolbarItemAppearance, WithAriaLabelEnforcement } from "./types";
import { DataTrackingId } from "../../types";
import { useOverFlowItemContext } from "./internal/ToolbarItemOverflowContext";
import Tooltip from "../Tooltip";
import { ToolbarItemWrapper } from "./internal/ToolbarItemWrapper";
import { useTrackingId } from "../../hooks";
import { useToolbarSize } from "./internal/ToolbarContext";

import cx from "classnames";
import styles from "./Toolbar.module.scss";

/**
 * Base props for the ToolbarButtonLink component
 * @extends Omit<ButtonLinkProps, "appearance" | "aria-label" | "children">
 */
type ToolbarButtonLinkBaseProps = Omit<
  ButtonLinkProps,
  "appearance" | "aria-label" | "children"
> & {
  /**
   * Visual appearance of the button link
   * @default ghost
   */
  appearance?: ToolbarItemAppearance;
};

/**
 * Props for the ToolbarButtonLink component
 * @extends WithAriaLabelEnforcement<ToolbarButtonLinkBaseProps>
 */
export type ToolbarButtonLinkProps =
  WithAriaLabelEnforcement<ToolbarButtonLinkBaseProps> & DataTrackingId;

/**
 * ToolbarButtonLink component for link actions within a toolbar.
 *
 * Features:
 * - Link functionality with href support
 * - Designed specifically for toolbar integration
 * - Consistent styling with other toolbar items
 * - Automatic tooltip for icon-only links
 * - Overflow menu support
 * - Accessibility enforcement for aria-labels
 * - Ghost and primary appearance options
 * - Proper anchor element semantics
 *
 * @example
 * <ToolbarButtonLink href="/help">
 *   Help
 * </ToolbarButtonLink>
 *
 * @example
 * <ToolbarButtonLink icon={ExternalLinkIcon} href="/docs" aria-label="Documentation" />
 */
export const ToolbarButtonLink = forwardRef<
  HTMLAnchorElement,
  ToolbarButtonLinkProps
>((props, _ref) => {
  const { appearance = "ghost", className, children, ...rest } = props;

  // Checks if this component is rendered inside the overflow menu and fallbacks when the context is unavailable
  const { isInOverflowMenu = false } = useOverFlowItemContext?.() || {};
  const size = useToolbarSize();

  const trackingId = useTrackingId({
    name: "ToolbarButtonLink",
    data: {
      href: props.href,
      disabled: props.disabled,
      "aria-label": props["aria-label"],
      children: props.children,
      target: props.target,
    },
    hasOverride: !!props["data-tracking-id"],
  });

  const buttonLinkClassNames = cx(className, styles["toolbar-button-item"]);

  const item = children ? (
    <ButtonLink
      data-tracking-id={trackingId}
      appearance={appearance}
      size={size}
      data-anv="toolbar-button-link"
      className={buttonLinkClassNames}
      {...rest}
    >
      {children}
    </ButtonLink>
  ) : (
    <Tooltip>
      <Tooltip.Trigger>
        <ButtonLink
          data-tracking-id={trackingId}
          appearance={appearance}
          size={size}
          data-anv="toolbar-button-link"
          className={buttonLinkClassNames}
          {...rest}
        />
      </Tooltip.Trigger>
      <Tooltip.Content>{rest["aria-label"]}</Tooltip.Content>
    </Tooltip>
  );

  if (isInOverflowMenu) {
    return item;
  }

  return (
    <ToolbarItemWrapper item={{ itemProps: props, itemType: "buttonLink" }}>
      {item}
    </ToolbarItemWrapper>
  );
});

ToolbarButtonLink.displayName = "ToolbarButtonLink";
